import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Button, styled, Tooltip } from "@mui/material";
import { Check2Icon, PlanIcon } from "../../assets/icons";
import { PlanItemResponse } from "../../openapi/client";
import { useMealCardData } from "./useMealCardData";

const MealCard = styled(Box)`
  background: white;
  border-radius: 16px;
  border: 1px solid var(--base-100);
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.04), 0px 2px 0px 0px var(--base-100);
  min-width: 320px;
  width: 320px;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  transform-origin: center center;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 900px) {
    min-width: 380px;
    width: 380px;
  }

  &:focus-visible {
    outline: 2px solid rgba(41, 188, 98, 0.5);
    outline-offset: 2px;
  }
  &:hover {
    border-color: var(--base-300);
    box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.04), 0px 2px 0px 0px var(--base-300);
  }
  &:hover button {
    background-color: var(--primary-default);
    color: var(--primary-text);
  }
`;

interface DesktopMealCardProps {
  meal?: PlanItemResponse;
  title?: string;
  subtitle?: string;
  mealTypes?: string;
  features?: string[];
  price?: string;
  partner?: string;
  imageUrl?: string;
}

export const DesktopMealCard: React.FC<DesktopMealCardProps> = (props) => {
  const { cardTitle, cardSubtitle, cardMealTypes, cardPrice, cardPartner, cardImageUrl, cardFeatures, buyHandler, t } =
    useMealCardData(props);

  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const [isSubtitleTruncated, setIsSubtitleTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const subtitleRef = useRef<HTMLSpanElement>(null);

  // Check for text truncation
  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current && cardMealTypes) {
        const element = textRef.current;
        const isOverflowing = element.scrollHeight > element.clientHeight;
        setIsTextTruncated(isOverflowing);
      }
      if (subtitleRef.current && cardSubtitle) {
        const element = subtitleRef.current;
        const isOverflowing = element.scrollWidth > element.clientWidth;
        setIsSubtitleTruncated(isOverflowing);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [cardMealTypes, cardSubtitle]);

  return (
    <MealCard onClick={buyHandler}>
      <Box
        sx={{
          padding: { sm: "28px 20px", md: "32px 24px" },
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Desktop Layout: Vertical */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", mb: { sm: "22px", md: "20px" } }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: "normal",
                color: "var(--font-dark)",
              }}
            >
              {cardTitle}
            </Typography>
            <Tooltip
              title={cardSubtitle || ""}
              disableHoverListener={!isSubtitleTruncated}
              disableFocusListener={!isSubtitleTruncated}
              disableTouchListener={!isSubtitleTruncated}
            >
              <Typography
                ref={subtitleRef}
                sx={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "normal",
                  color: "var(--font-light)",
                  maxHeight: "20px",
                }}
              >
                {cardSubtitle}
              </Typography>
            </Tooltip>
          </Box>
          {/* Image with partner badge overlay */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: { sm: "150px", md: "160px" },
              borderRadius: "12px",
              backgroundImage: `url(${cardImageUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                bottom: "8px",
                right: "8px",
                background: "white",
                borderRadius: "6px",
                padding: "4px 8px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                boxShadow: "0px 2px 4px 0px rgba(0,0,0,0.1)",
              }}
            >
              <Box
                sx={{
                  width: "18px",
                  height: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--success-default)",
                  "& svg": {
                    width: "18px",
                    height: "18px",
                    fill: "var(--success-default)",
                  },
                }}
              >
                <PlanIcon />
              </Box>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "12px",
                  lineHeight: "1.5",
                  color: "var(--success-default)",
                }}
              >
                {cardPartner}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Features Section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flexGrow: 1 }}>
          {/* Meal Types */}
          <Tooltip
            title={cardMealTypes || ""}
            disableHoverListener={!isTextTruncated}
            disableFocusListener={!isTextTruncated}
            disableTouchListener={!isTextTruncated}
          >
            <Typography
              ref={textRef}
              sx={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "1.5",
                color: "var(--font-dark)",
                maxHeight: "54px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
            >
              {cardMealTypes}
            </Typography>
          </Tooltip>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", padding: "4px 8px" }}>
            {cardFeatures.map((feature, index) => (
              <Box key={index} sx={{ display: "flex", alignItems: "start", gap: "8px" }}>
                <Box
                  sx={{
                    width: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: "var(--success-default)",
                    "& svg": {
                      width: "24px",
                      height: "24px",
                      fill: "var(--success-default)",
                    },
                  }}
                >
                  <Check2Icon />
                </Box>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "1.5",
                    color: "var(--font-default)",
                    flex: 1,
                  }}
                >
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom Section - Divider and Price */}
        <Box sx={{ mt: "auto", pt: { sm: "22px", md: "20px" } }}>
          {/* Divider */}
          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: "var(--base-50)",
              mb: { sm: "22px", md: "20px" },
            }}
          />

          {/* Price Section */}
          <Box sx={{ padding: "4px 0px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "end",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, lineHeight: "normal", color: "var(--font-dark)" }}>
                  {t("startingFrom")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "end", gap: "4px" }}>
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 700,
                      fontSize: "32px",
                      lineHeight: "29px",
                      color: "var(--success-dark)",
                    }}
                  >
                    {cardPrice}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 600,
                      fontSize: "18px",
                      lineHeight: "normal",
                      color: "var(--success-dark)",
                    }}
                  >
                    {t("currency")}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 400,
                      fontSize: "18px",
                      lineHeight: "normal",
                      color: "var(--font-light)",
                    }}
                  >
                    {t("perDay")}
                  </Typography>
                </Box>
              </Box>
              <Button
                sx={{
                  fontWeight: 600,
                  backgroundColor: "var(--primary-lighter)",
                  color: "var(--primary-dark)",
                  "&:hover": {
                    backgroundColor: "var(--primary-default)",
                    color: "var(--primary-text)",
                  },
                  fontSize: "14px",
                  lineHeight: "20px",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  textTransform: "none",
                  minWidth: "auto",
                  flexShrink: 0,
                }}
              >
                {t("selectPlan")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </MealCard>
  );
};
